import { CORNIS, SKYSPECS, SKYSPECS_ORG } from 'utils/constants';

/**
 * Two concepts live here:
 *
 * **Classification** (`isSkySpecsInspection` / `isCornisInspection`) is deliberately NOT gated
 * behind `INSPECTION_VENDOR_NAMES` so that behavior is consistent between users with and without inspection_vendor_names toggle.
 *
 * **Display** (`getInspectionVendorDisplayName`) IS gated, via a required `hasVendorNames`
 * argument. Derive legacy vendor names from org names to keep non-toggle users' experience consistent.
 */

type InspectionVendorFields = {
  vendor?: { name?: string | null } | null;
  vendorOrganization?: { id: string; name?: string | null } | null;
};

/**
 * The legacy InspectionVendor names a toggle-off user can be shown.
 * Filter dropdown names reflect backend values - not translated intentionally
 */
const LEGACY_VENDOR_NAMES = {
  SKYSPECS: 'SkySpecs',
  CORNIS: 'Cornis',
  THIRD_PARTY: 'Third Party',
} as const;

/**
 * The legacy InspectionVendor name to show for an Inspection, mapping a `vendorOrganization` back
 * onto the legacy vocabulary. Fall back to legacy vendor value.
 */
function getLegacyVendorName(
  inspection?: InspectionVendorFields | null
): string | null | undefined {
  if (!inspection?.vendorOrganization) {
    return inspection?.vendor?.name;
  }

  if (isSkySpecsInspection(inspection)) {
    return LEGACY_VENDOR_NAMES.SKYSPECS;
  }

  if (isCornisInspection(inspection)) {
    return LEGACY_VENDOR_NAMES.CORNIS;
  }

  // Any other Organization is a third party as far as the legacy vocabulary is concerned, and is
  // what the create form already writes as the Inspection's legacy vendor.
  return LEGACY_VENDOR_NAMES.THIRD_PARTY;
}

export function getInspectionVendorDisplayName(
  inspection: InspectionVendorFields | null | undefined,
  hasVendorNames: boolean
): string | null | undefined {
  if (!hasVendorNames) {
    return getLegacyVendorName(inspection);
  }

  return inspection?.vendorOrganization?.name ?? inspection?.vendor?.name;
}

export function isSkySpecsInspection(inspection?: InspectionVendorFields | null): boolean {
  if (inspection?.vendorOrganization) {
    return inspection.vendorOrganization.id === SKYSPECS_ORG;
  }

  return inspection?.vendor?.name?.toLowerCase() === SKYSPECS;
}

export function isCornisInspection(inspection?: InspectionVendorFields | null): boolean {
  if (inspection?.vendorOrganization) {
    return inspection.vendorOrganization.name?.toLowerCase() === CORNIS;
  }

  return inspection?.vendor?.name?.toLowerCase() === CORNIS;
}
